import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { STORAGE_PREFIX } from '@/lib/brand';
import {
  Currency,
  Invoice,
  PaymentEvent,
  PaymentMethod,
  PaymentModuleConfig,
  PaymentPlan,
  PaymentProviderId,
  PaymentStatus,
  PaymentTransaction,
  PlanEntitlements,
  Subscription,
  SubscriptionStatus,
  UNLIMITED_ENTITLEMENTS,
} from '../types';
import { paymentOptionKey } from '../types';
import { transition } from './paymentMachine';
import {
  DEFAULT_PAYMENT_CONFIG,
  DEFAULT_PLANS,
  legacyMethodsToOptionEnabled,
} from '../config/defaults';

/**
 * ─── Store du module de paiement ─────────────────────────────────────────────
 *
 * Source de vérité UI du module : config admin, plans, tentative de paiement
 * en cours, abonnement, historique, factures. Persisté localement.
 *
 * CONTRAT BACKEND : quand l'API existera, `config`/`plans` seront hydratés
 * depuis `GET /public/config/payments`, et `subscription`/`transactions`/
 * `invoices` depuis les endpoints authentifiés. La forme du store ne change
 * pas — seuls les points d'hydratation (actions `hydrate*`) seront branchés.
 */

/** Tentative de paiement en cours (survit à la redirection provider). */
export interface PaymentAttempt {
  reference: string;
  planId: string;
  providerId: PaymentProviderId;
  method: PaymentMethod;
  /** Numéro du payeur (mobile money) — conservé pour le « Réessayer ». */
  phone?: string;
  status: PaymentStatus;
  orderNumber: string | null;
  startedAt: string;
  error: string | null;
}

/** Type de fonctionnalité soumise au quota gratuit quotidien. */
export type QuotaKind = 'practice' | 'simulation';

/** Compteurs d'essais gratuits du jour (device-local en attendant l'API). */
export interface QuotaUsage {
  /** Jour local YYYY-MM-DD auquel les compteurs se rapportent. */
  date: string;
  practice: number;
  simulation: number;
  /** Dernière consommation : évite de recompter un simple refresh/reprise. */
  lastKey: string | null;
  lastKind: QuotaKind | null;
  lastAt: string | null;
}

interface PaymentState {
  config: PaymentModuleConfig;
  plans: PaymentPlan[];
  subscription: Subscription | null;
  attempt: PaymentAttempt | null;
  transactions: PaymentTransaction[];
  invoices: Invoice[];
  /** Limites d'essais gratuits/jour (hydratées depuis la config publique). */
  freeQuota: { dailyPractice: number; dailySimulation: number };
  usage: QuotaUsage;
}

interface PaymentActions {
  // Config (admin)
  updateConfig: (patch: Partial<PaymentModuleConfig>) => void;
  setProviderEnabled: (id: PaymentProviderId, enabled: boolean) => void;
  /** Kill-switch d'une OPTION de paiement (couple moyen × fournisseur). */
  setOptionEnabled: (
    method: PaymentMethod,
    provider: PaymentProviderId,
    enabled: boolean,
  ) => void;

  // Hydratation depuis le backend (source de vérité globale)
  hydrateFromServer: (payload: {
    config: Partial<PaymentModuleConfig> | null;
    plans: PaymentPlan[] | null;
    freeLimits?: { dailyPractice: number; dailySimulation: number };
  }) => void;

  // Quota quotidien (gratuit OU limites du plan de l'abonné)
  /**
   * Limites effectives de l'utilisateur courant : entitlements de son plan
   * s'il est abonné actif, sinon quota gratuit global. null = illimité.
   */
  effectiveLimits: () => PlanEntitlements;
  /**
   * Tente de consommer un essai pour `key` (route de la session).
   * true = accès accordé (toujours si la limite effective est illimitée).
   * Un même `key` reconsommé dans les 30 min ne compte qu'une fois
   * (refresh, reprise de session, double-mount StrictMode).
   */
  consumeQuota: (kind: QuotaKind, key: string) => boolean;
  /** Essais restants aujourd'hui pour ce type — null = illimité. */
  remainingQuota: (kind: QuotaKind) => number | null;

  // Plans (admin)
  upsertPlan: (plan: PaymentPlan) => void;
  removePlan: (planId: string) => void;

  // Tentative de paiement
  beginAttempt: (
    attempt: Omit<PaymentAttempt, 'status' | 'orderNumber' | 'startedAt' | 'error'>,
  ) => void;
  applyEvent: (event: PaymentEvent, patch?: Partial<PaymentAttempt>) => PaymentStatus | null;
  clearAttempt: () => void;

  // Résultats
  recordTransaction: (tx: PaymentTransaction) => void;
  activateSubscription: (args: {
    plan: PaymentPlan;
    providerId: PaymentProviderId;
    method: PaymentMethod;
    paymentMethodLabel: string | null;
    transactionReference: string;
    customer: { name: string; email: string };
  }) => { subscription: Subscription; invoice: Invoice };
  /** Hydrate l'abonnement rattaché au compte (depuis le backend). */
  setSubscription: (subscription: Subscription | null) => void;
  refreshSubscriptionStatus: () => void;
  resetModuleData: () => void;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/** Jour LOCAL (pas UTC) : le quota se réinitialise à minuit local. */
function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const EMPTY_USAGE: QuotaUsage = {
  date: '',
  practice: 0,
  simulation: 0,
  lastKey: null,
  lastKind: null,
  lastAt: null,
};

/** Fenêtre pendant laquelle re-consommer la même session ne recompte pas. */
const QUOTA_REENTRY_GRACE_MS = 30 * 60 * 1000;

/**
 * Garantit qu'un plan (venu du serveur, d'un vieux localStorage ou de
 * l'éditeur admin) possède des entitlements valides. Les plans créés avant
 * l'introduction des entitlements sont considérés ILLIMITÉS — même
 * comportement qu'avant, jamais de verrouillage surprise d'un abonné payant.
 */
function normalizePlan(plan: PaymentPlan): PaymentPlan {
  const raw = (plan as Partial<PaymentPlan>).entitlements;
  const clean = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : null;
  return {
    ...plan,
    entitlements: {
      dailyPracticeLimit: clean(raw?.dailyPracticeLimit),
      dailySimulationLimit: clean(raw?.dailySimulationLimit),
    },
  };
}

export const usePaymentStore = create<PaymentState & PaymentActions>()(
  persist(
    (set, get) => ({
      config: DEFAULT_PAYMENT_CONFIG,
      plans: DEFAULT_PLANS,
      subscription: null,
      attempt: null,
      transactions: [],
      invoices: [],
      freeQuota: { dailyPractice: 3, dailySimulation: 1 },
      usage: EMPTY_USAGE,

      updateConfig: (patch) =>
        set((s) => ({ config: { ...s.config, ...patch } })),

      hydrateFromServer: ({ config, plans, freeLimits }) =>
        set((s) => ({
          // null = jamais configuré côté serveur → on garde l'état local
          // (défauts : module désactivé). Fusion profonde pour survivre aux
          // ajouts futurs de champs de config.
          config: config
            ? {
                ...DEFAULT_PAYMENT_CONFIG,
                ...config,
                providers: {
                  ...DEFAULT_PAYMENT_CONFIG.providers,
                  ...(config.providers ?? {}),
                },
                // Kill-switch par option : défauts + migration de l'ancien
                // `methods` (rétro-compat) + overrides serveur (qui priment,
                // ex. carte FlexPay forcée OFF si non configurée).
                optionEnabled: {
                  ...DEFAULT_PAYMENT_CONFIG.optionEnabled,
                  ...legacyMethodsToOptionEnabled(
                    (config as { methods?: Record<string, { enabled?: boolean }> }).methods,
                  ),
                  ...(config.optionEnabled ?? {}),
                },
              }
            : s.config,
          plans: plans ? plans.map(normalizePlan) : s.plans,
          freeQuota: freeLimits ?? s.freeQuota,
        })),

      effectiveLimits: () => {
        const s = get();
        if (s.subscription?.status === SubscriptionStatus.ACTIVE) {
          const plan = s.plans.find((p) => p.id === s.subscription!.planId);
          // Plan introuvable (supprimé par l'admin…) → illimité : on ne
          // verrouille jamais un abonné payant par accident.
          return plan ? normalizePlan(plan).entitlements : UNLIMITED_ENTITLEMENTS;
        }
        return {
          dailyPracticeLimit: s.freeQuota.dailyPractice,
          dailySimulationLimit: s.freeQuota.dailySimulation,
        };
      },

      consumeQuota: (kind, key) => {
        const s = get();
        const limits = s.effectiveLimits();
        const limit =
          kind === 'practice' ? limits.dailyPracticeLimit : limits.dailySimulationLimit;
        if (limit === null) return true; // illimité : rien à décompter

        const today = todayKey();
        const usage: QuotaUsage =
          s.usage.date === today ? s.usage : { ...EMPTY_USAGE, date: today };

        // Reprise de la même session (refresh, StrictMode…) : déjà payée.
        if (
          usage.lastKey === key &&
          usage.lastKind === kind &&
          usage.lastAt &&
          Date.now() - new Date(usage.lastAt).getTime() < QUOTA_REENTRY_GRACE_MS
        ) {
          return true;
        }

        if (usage[kind] >= limit) {
          set({ usage });
          return false;
        }

        set({
          usage: {
            ...usage,
            [kind]: usage[kind] + 1,
            lastKey: key,
            lastKind: kind,
            lastAt: new Date().toISOString(),
          },
        });
        return true;
      },

      remainingQuota: (kind) => {
        const s = get();
        const limits = s.effectiveLimits();
        const limit =
          kind === 'practice' ? limits.dailyPracticeLimit : limits.dailySimulationLimit;
        if (limit === null) return null; // illimité
        const used = s.usage.date === todayKey() ? s.usage[kind] : 0;
        return Math.max(0, limit - used);
      },

      setProviderEnabled: (id, enabled) =>
        set((s) => ({
          config: {
            ...s.config,
            providers: { ...s.config.providers, [id]: enabled },
          },
        })),

      setOptionEnabled: (method, provider, enabled) =>
        set((s) => ({
          config: {
            ...s.config,
            optionEnabled: {
              ...s.config.optionEnabled,
              [paymentOptionKey(method, provider)]: enabled,
            },
          },
        })),

      upsertPlan: (rawPlan) =>
        set((s) => {
          const plan = normalizePlan(rawPlan);
          const exists = s.plans.some((p) => p.id === plan.id);
          return {
            plans: exists
              ? s.plans.map((p) => (p.id === plan.id ? plan : p))
              : [...s.plans, plan],
          };
        }),

      removePlan: (planId) =>
        set((s) => ({ plans: s.plans.filter((p) => p.id !== planId) })),

      beginAttempt: (attempt) =>
        set({
          attempt: {
            ...attempt,
            status: PaymentStatus.IDLE,
            orderNumber: null,
            startedAt: new Date().toISOString(),
            error: null,
          },
        }),

      /**
       * Applique un événement à la tentative courante via la machine d'états.
       * Retourne le nouveau statut, ou null si la transition est interdite
       * (elle est alors ignorée — jamais d'état incohérent).
       */
      applyEvent: (event, patch) => {
        const { attempt } = get();
        if (!attempt) return null;
        const next = transition(attempt.status, event);
        if (next === null) return null;
        set({ attempt: { ...attempt, ...patch, status: next } });
        return next;
      },

      clearAttempt: () => set({ attempt: null }),

      recordTransaction: (tx) =>
        set((s) => ({
          transactions: [
            tx,
            ...s.transactions.filter((t) => t.reference !== tx.reference),
          ].slice(0, 100),
        })),

      activateSubscription: ({
        plan,
        providerId,
        method,
        paymentMethodLabel,
        transactionReference,
        customer,
      }) => {
        const { subscription } = get();
        // Renouvellement : on prolonge depuis l'expiration si elle est future,
        // sinon on repart d'aujourd'hui.
        const base =
          subscription &&
          subscription.status === SubscriptionStatus.ACTIVE &&
          new Date(subscription.expiresAt) > new Date()
            ? new Date(subscription.expiresAt)
            : new Date();
        const expiresAt = addDays(base, plan.periodDays);
        const now = new Date();

        const invoice: Invoice = {
          id: `INV-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}-${transactionReference.slice(-6).toUpperCase()}`,
          transactionReference,
          planId: plan.id,
          planName: plan.name,
          amount: plan.price,
          currency: plan.currency as Currency,
          issuedAt: now.toISOString(),
          periodStart: base.toISOString(),
          periodEnd: expiresAt.toISOString(),
          customerName: customer.name,
          customerEmail: customer.email,
        };

        const newSubscription: Subscription = {
          planId: plan.id,
          status: SubscriptionStatus.ACTIVE,
          startedAt: now.toISOString(),
          expiresAt: expiresAt.toISOString(),
          providerId,
          method,
          paymentMethodLabel,
        };

        set((s) => ({
          subscription: newSubscription,
          invoices: [invoice, ...s.invoices].slice(0, 100),
        }));

        return { subscription: newSubscription, invoice };
      },

      setSubscription: (subscription) => set({ subscription }),

      /** À appeler au montage : fait expirer l'abonnement si la date est passée. */
      refreshSubscriptionStatus: () => {
        const { subscription } = get();
        if (!subscription || subscription.status !== SubscriptionStatus.ACTIVE) return;
        if (new Date(subscription.expiresAt) <= new Date()) {
          set({
            subscription: { ...subscription, status: SubscriptionStatus.EXPIRED },
          });
        }
      },

      resetModuleData: () =>
        set({
          subscription: null,
          attempt: null,
          transactions: [],
          invoices: [],
          usage: EMPTY_USAGE,
        }),
    }),
    {
      name: `${STORAGE_PREFIX}-payment`,
      /**
       * Fusion profonde de la config persistée avec les défauts : si un champ
       * de config est ajouté dans une future version, les stores déjà
       * persistés restent valides (jamais de `providers` undefined…).
       */
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<PaymentState>;
        return {
          ...current,
          ...p,
          config: {
            ...DEFAULT_PAYMENT_CONFIG,
            ...(p.config ?? {}),
            providers: {
              ...DEFAULT_PAYMENT_CONFIG.providers,
              ...(p.config?.providers ?? {}),
            },
            optionEnabled: {
              ...DEFAULT_PAYMENT_CONFIG.optionEnabled,
              ...legacyMethodsToOptionEnabled(
                (p.config as { methods?: Record<string, { enabled?: boolean }> } | undefined)?.methods,
              ),
              ...(p.config?.optionEnabled ?? {}),
            },
          },
          // Plans persistés avant l'introduction des entitlements → illimité.
          plans: (p.plans ?? current.plans).map(normalizePlan),
        };
      },
    },
  ),
);
